import helpers from '@debbie/common/dist/economy/helpers/index.js';
import { Entity, Site, formatNumber } from '@debbie/common/dist/index.js';
import { useProposalHistoryByCase } from '@debbie/cortex/dist/agents/index.js';
import { useCaseVouchersByCase } from '@debbie/cortex/dist/case-vouchers/index.js';
import { useCase } from '@debbie/cortex/dist/cases/index.js';
import { useConnectCaseMappingOnCase } from '@debbie/cortex/dist/connect/index.js';
import { useDocumentsByReference } from '@debbie/cortex/dist/documents/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { useInteractionsByReference, } from '@debbie/cortex/dist/interactions/index.js';
import { useRole } from '@debbie/cortex/dist/me/index.js';
import { usePaymentPlansByCaseId } from '@debbie/cortex/dist/paymentPlans/index.js';
import { useTribunalCasesByCaseId } from '@debbie/cortex/dist/tribunal/index.js';
import { useUpdatesByRefId } from '@debbie/cortex/dist/updates/index.js';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import Errors from '../Errors.js';
import Input from '../Input.js';
import PlaceholderLoader from '../PlaceholderLoader.js';
import CaseHistoryFilter, { EMPTY_HISTORY_FILTER, isCategoryOn, isTagOn, isTypeOn, } from './CaseHistoryFilter.js';
import HistItemTypes from './HistItemTypes.js';
import { HistItemType } from './itemTypes.js';
const tagsOf = (item) => item.type === HistItemType.Interactions ? item.data.tags : [];
const categoryOf = (item) => {
    switch (item.type) {
        case HistItemType.PaymentPlan:
            return 'paymentPlans';
        case HistItemType.TribunalCase:
            return 'tribunal';
        case HistItemType.Document:
            return 'documents';
        case HistItemType.AgentProposal:
            return 'agentProposals';
        case HistItemType.Updates:
        case HistItemType.ConnectUpdate:
            return 'updates';
        case HistItemType.Interactions:
            return 'interactions';
        case HistItemType.VoucherCollection:
            return 'economy';
        case HistItemType.CaseStatus:
        case HistItemType.CaseCreation:
            return 'caseStatus';
    }
};
// Hidden tags dominate: an interaction carrying one is shown only when one
// of its hidden tags is switched on. Otherwise a tagged interaction is shown
// while any of its tags is on.
const isShown = (filter, item) => {
    if (!isCategoryOn(filter, categoryOf(item))) {
        return false;
    }
    if (item.type !== HistItemType.Interactions) {
        return true;
    }
    if (!isTypeOn(filter, item.data.type)) {
        return false;
    }
    const tags = tagsOf(item);
    if (tags.length === 0) {
        return true;
    }
    const hasHidden = tags.some((tag) => tag.hiddenByDefault);
    return tags.some((tag) => tag.hiddenByDefault === hasHidden && isTagOn(filter, tag));
};
const itemTime = (item) => moment(item.date || item.timespan?.from).valueOf();
const tagsWithCounts = (interactionItems) => {
    const tags = new Map();
    interactionItems.flatMap(tagsOf).forEach((tag) => {
        const entry = tags.get(tag.id);
        if (entry) {
            entry.count++;
        }
        else {
            tags.set(tag.id, { ...tag, count: 1 });
        }
    });
    return Array.from(tags.values());
};
const typesWithCounts = (interactionItems) => {
    const counts = new Map();
    interactionItems.forEach((item) => {
        if (item.type !== HistItemType.Interactions) {
            return;
        }
        counts.set(item.data.type, (counts.get(item.data.type) ?? 0) + 1);
    });
    return Array.from(counts, ([type, count]) => ({ type, count }));
};
const CaseHistory = (props) => {
    const meRole = useRole();
    const lang = useSelector((s) => s.lang);
    const site = useSelector((s) => s.site);
    const singleCaseLoader = useCase(props.caseId);
    const singleCase = h.hasData(singleCaseLoader) ? singleCaseLoader.data : null;
    const caseworkerCaseId = site === Site.Caseworker ? singleCase?.caseId : undefined;
    const plansLoadable = usePaymentPlansByCaseId(caseworkerCaseId);
    const tribunalLoadable = useTribunalCasesByCaseId(caseworkerCaseId);
    const documentsLoadable = useDocumentsByReference(caseworkerCaseId);
    const paymentPlanItems = useMemo(() => site === Site.Caseworker && h.hasData(plansLoadable)
        ? (plansLoadable.data ?? []).map((plan) => ({
            key: plan.paymentPlanId,
            type: HistItemType.PaymentPlan,
            date: plan.createdAt,
            data: plan,
        }))
        : [], [site, plansLoadable]);
    const tribunalItems = useMemo(() => site === Site.Caseworker && h.hasData(tribunalLoadable)
        ? tribunalLoadable.data.map((tribunalCase) => ({
            key: tribunalCase.id,
            type: HistItemType.TribunalCase,
            date: tribunalCase.createdAt,
            data: tribunalCase,
        }))
        : [], [site, tribunalLoadable]);
    const documentItems = useMemo(() => site === Site.Caseworker && h.hasData(documentsLoadable)
        ? documentsLoadable.data.map((document) => ({
            key: document.documentId,
            type: HistItemType.Document,
            date: document.createdAt,
            data: document,
        }))
        : [], [site, documentsLoadable]);
    const proposalsLoadable = useProposalHistoryByCase(site === Site.Caseworker ? singleCase?.caseId : undefined);
    const proposalItems = useMemo(() => site === Site.Caseworker && h.hasData(proposalsLoadable)
        ? (proposalsLoadable.data ?? []).map((proposal) => ({
            key: proposal.id,
            type: HistItemType.AgentProposal,
            date: proposal.proposedAt,
            data: proposal,
        }))
        : [], [site, proposalsLoadable]);
    const caseInteractionsLoadable = useInteractionsByReference(Entity.Case, singleCase?.caseId);
    const caseInteractions = h.hasData(caseInteractionsLoadable)
        ? caseInteractionsLoadable.data
        : null;
    const updatesLoadable = useUpdatesByRefId(singleCase?.caseId);
    const caseUpdates = h.hasData(updatesLoadable) ? updatesLoadable.data : null;
    const connectCaseMappings = useConnectCaseMappingOnCase(site === Site.Caseworker ? singleCase?.caseId : undefined);
    const caseVouchers_ = useCaseVouchersByCase(singleCase?.caseId);
    const _caseVouchers = h.hasData(caseVouchers_) ? caseVouchers_.data : null;
    const isLoading = useSelector((s) => {
        return (singleCase &&
            (h.isLoading(singleCaseLoader) || h.isLoading(updatesLoadable)));
    }) || h.isLoading(caseInteractionsLoadable);
    const [fromVouchers, setFromVouchers] = useState(null);
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState(EMPTY_HISTORY_FILTER);
    const [items, setItems] = useState({
        [HistItemType.Interactions]: [],
        [HistItemType.Updates]: [],
        [HistItemType.VoucherCollection]: [],
        [HistItemType.CaseCreation]: [],
        [HistItemType.CaseStatus]: [],
        [HistItemType.ConnectUpdate]: [],
    });
    useEffect(() => {
        if (site === Site.Debtor
            ? caseInteractions && _caseVouchers && singleCase
            : singleCase && (caseInteractions || caseUpdates || _caseVouchers)) {
            setItems({
                [HistItemType.Interactions]: getInteractions(),
                [HistItemType.Updates]: getUpdates(),
                [HistItemType.VoucherCollection]: getCaseVouchers(),
                [HistItemType.CaseCreation]: getCaseCreation(),
                [HistItemType.CaseStatus]: getCaseStatus(),
                [HistItemType.ConnectUpdate]: getConnectUpdates(),
            });
        }
    }, [
        caseInteractions,
        caseUpdates,
        _caseVouchers,
        singleCase,
        fromVouchers,
        connectCaseMappings.loadingState,
    ]);
    const allItems = useMemo(() => [
        ...Object.values(items).flat(),
        ...proposalItems,
        ...paymentPlanItems,
        ...tribunalItems,
        ...documentItems,
    ], [items, proposalItems, paymentPlanItems, tribunalItems, documentItems]);
    const tags = useMemo(() => tagsWithCounts(items[HistItemType.Interactions]), [items]);
    const types = useMemo(() => typesWithCounts(items[HistItemType.Interactions]), [items]);
    const categoryCounts = useMemo(() => ({
        agentProposals: proposalItems.length,
        paymentPlans: paymentPlanItems.length,
        tribunal: tribunalItems.length,
        documents: documentItems.length,
        updates: items[HistItemType.Updates].length +
            items[HistItemType.ConnectUpdate].length,
        interactions: items[HistItemType.Interactions].length,
        economy: items[HistItemType.VoucherCollection].length,
        caseStatus: items[HistItemType.CaseStatus].length +
            items[HistItemType.CaseCreation].length,
    }), [items, proposalItems, paymentPlanItems, tribunalItems, documentItems]);
    const getInteractions = () => {
        if (caseInteractions) {
            return caseInteractions.map((i, index) => ({
                key: i.id || `interactions_${index}`,
                type: HistItemType.Interactions,
                date: `${i.createdAt || i.transacted}`,
                data: i,
            }));
        }
        return [];
    };
    const getUpdates = () => {
        if (caseUpdates) {
            return Object.values(caseUpdates
                .filter((u) => u.hiddenPermissionLevel !== meRole)
                .reduce((acc, update) => {
                const key = `${update.updateTypeId}${update.continuedAt && update.continuedAt.substr(0, 13)}${update.update}${update.createdAt.substr(0, 13)}${update.state}${update.taskId}${update?.data?.isCaseMergeDetails ? update.updateId : ''}`;
                if (Array.isArray(acc[key])) {
                    acc[key].push(update);
                }
                else {
                    acc[key] = [update];
                }
                return acc;
            }, {})).map((updates) => ({
                key: updates[0].updateId,
                type: HistItemType.Updates,
                timespan: {
                    from: updates[0].createdAt,
                    to: updates[0].continuedAt || null,
                },
                data: updates,
            }));
        }
        return [];
    };
    const getCaseStatus = () => {
        if (singleCase && singleCase.statusLog && site !== Site.Debtor) {
            return singleCase.statusLog.map((s) => ({
                key: s.createdAt + singleCase.caseId,
                type: HistItemType.CaseStatus,
                date: s.createdAt,
                data: s,
            }));
        }
        return [];
    };
    const getCaseVouchers = () => {
        if (_caseVouchers) {
            let kaseVouchers = _caseVouchers;
            // Debtors see what they owe, as in their list of vouchers.
            if (site === Site.Debtor) {
                kaseVouchers = helpers.vouchers
                    .postProcessing(kaseVouchers, {
                    includeIrrecoverableVouchers: false,
                    includeDeletedVouchers: false,
                    includeExpiredVouchers: false,
                })
                    .filter(helpers.vouchers.isListed);
            }
            const voucherChildren = kaseVouchers.reduce((acc, kaseVoucher) => {
                const key = kaseVoucher.referenceCaseVoucherId;
                if (Array.isArray(acc[key])) {
                    acc[key].push(kaseVoucher);
                }
                else {
                    acc[key] = [kaseVoucher];
                }
                return acc;
            }, {});
            return Object.values(kaseVouchers
                .map((v) => {
                return {
                    ...v,
                    children: voucherChildren[v.caseVoucherId],
                };
            })
                .reduce((acc, kaseVoucher) => {
                const key = kaseVoucher.createdAt.substr(0, 13);
                if (Array.isArray(acc[key])) {
                    acc[key].push(kaseVoucher);
                }
                else {
                    acc[key] = [kaseVoucher];
                }
                return acc;
            }, {})).map((kaseVouchers) => ({
                key: kaseVouchers[0].caseVoucherId,
                type: HistItemType.VoucherCollection,
                date: kaseVouchers[0].createdAt || kaseVouchers[0].date,
                data: {
                    categories: Array.from(new Set(kaseVouchers.map((v) => v.category))),
                    caseVouchers: kaseVouchers,
                    date: kaseVouchers[0].date,
                },
            }));
        }
        return [];
    };
    const getCaseCreation = () => {
        if (singleCase) {
            return [
                {
                    key: '',
                    type: HistItemType.CaseCreation,
                    date: singleCase.createdAt,
                    data: {
                        caseCreation: true,
                        createdBy: singleCase.createdBy ?? null,
                    },
                },
            ];
        }
        return [];
    };
    const getConnectUpdates = () => {
        return h.hasData(connectCaseMappings)
            ? connectCaseMappings.data.flatMap((mapping) => mapping.updates.map((u) => {
                return {
                    key: u.id,
                    type: HistItemType.ConnectUpdate,
                    date: u.createdAt,
                    data: {
                        mapping,
                        update: u,
                    },
                };
            }))
            : [];
    };
    // Newest first. The timestamp is parsed once per item rather than twice per
    // comparison, since this reruns on every keystroke in the search field.
    const visibleItems = useMemo(() => {
        const shown = allItems.filter((item) => isShown(filter, item));
        const q = query.toLowerCase();
        const matching = q
            ? shown.filter((item) => {
                let labels = '';
                switch (item.type) {
                    case HistItemType.PaymentPlan: {
                        const config = item.data.config;
                        const method = config == null
                            ? ''
                            : config.method === 'amount'
                                ? `${lang.paymentPlans.byAmount}: ${formatNumber(config.amount)} ${singleCase?.currency}`
                                : `${lang.paymentPlans.byDuration}: ${config.duration} ${lang.agentProposalBodies.installments}`;
                        const total = formatNumber(item.data.plan.reduce((sum, entry) => sum + entry.amount, 0));
                        labels = `${lang.history.paymentPlanCreated} ${method} ${item.data.plan.length} ${lang.agentProposalBodies.installments} ${lang.paymentPlans.totalPayment}: ${total} ${singleCase?.currency}`;
                        break;
                    }
                    case HistItemType.TribunalCase:
                        labels = `${lang.history.tribunalCaseCreated} ${lang.tribunalCaseTypes[item.data.type]}`;
                        break;
                    case HistItemType.Document:
                        labels = `${lang.history.documentAdded} ${lang.documentTypes[item.data.type]}`;
                        break;
                }
                return `${JSON.stringify(item)} ${labels}`.toLowerCase().includes(q);
            })
            : shown;
        return matching
            .map((item) => ({ item, at: itemTime(item) }))
            .sort((a, b) => b.at - a.at)
            .map(({ item }) => item);
    }, [allItems, filter, query, lang, singleCase?.currency]);
    if (!singleCase) {
        return null;
    }
    return (React.createElement(HistStyled, null,
        React.createElement(HistHeadline, null,
            React.createElement("h2", null, lang.history.history),
            React.createElement(Input, { value: query, onChange: (e) => {
                    setQuery(e.target.value);
                }, placeholder: lang.history.searchHistory }),
            React.createElement(CaseHistoryFilter, { categoryCounts: categoryCounts, types: types, 
                // Tag names are the tenant's own labels; debtors and creditors get
                // the default view without a way to surface hidden interactions.
                tags: site === Site.Caseworker ? tags : [], filter: filter, onChange: setFilter })),
        site === Site.Caseworker && h.isError(plansLoadable) && (React.createElement(Errors, { errors: [plansLoadable.error.message] })),
        site === Site.Caseworker &&
            h.isLoading(plansLoadable) &&
            !h.hasData(plansLoadable) && React.createElement(PlaceholderLoader, null),
        site === Site.Caseworker && h.isError(tribunalLoadable) && (React.createElement(Errors, { errors: [tribunalLoadable.error.message] })),
        site === Site.Caseworker &&
            h.isLoading(tribunalLoadable) &&
            !h.hasData(tribunalLoadable) && React.createElement(PlaceholderLoader, null),
        site === Site.Caseworker && h.isError(documentsLoadable) && (React.createElement(Errors, { errors: [documentsLoadable.error.message] })),
        site === Site.Caseworker &&
            h.isLoading(documentsLoadable) &&
            !h.hasData(documentsLoadable) && React.createElement(PlaceholderLoader, null),
        site === Site.Caseworker && h.isError(proposalsLoadable) && (React.createElement(Errors, { errors: [proposalsLoadable.error.message] })),
        isLoading ? (React.createElement(React.Fragment, null,
            React.createElement("div", { style: {
                    marginLeft: '75px',
                } },
                React.createElement(PlaceholderLoader, { width: "80%" })),
            React.createElement("div", { style: {
                    marginLeft: '75px',
                } },
                React.createElement(PlaceholderLoader, { width: "45%" })),
            React.createElement("div", { style: {
                    marginLeft: '75px',
                } },
                React.createElement(PlaceholderLoader, { width: "60%" })))) : (visibleItems.map((h, index) => (React.createElement("div", { className: "history-entry", key: `${h.type}-${h.key}`, id: `ui-test-4a648d7d-${index}` },
            React.createElement(HistItemTypes, { historyItem: h, singleCase: singleCase, referenceId: singleCase.caseId, referenceType: Entity.Case })))))));
};
export default CaseHistory;
const HistStyled = styled.div `
  padding-bottom: 20px;
`;
const HistHeadline = styled.div `
  display: flex;
  align-items: center;
  margin-bottom: 10px;

  h2 {
    display: inline-block;
  }

  .iris-input-container {
    display: inline-block;
    margin: 0 ${THEME.GRID_SPACING}px;
    flex-grow: 2;

    input {
      box-shadow: none !important;
      padding: 14px 15px 2px 20px !important;
      border: none !important;
      border-radius: 20px;
      margin-bottom: 0;

      &:focus ~ .inp-placeholder {
        top: 5px !important;
      }
    }

    .inp-placeholder {
      left: 20px;
      top: 10px !important;
    }
  }
`;
//# sourceMappingURL=CaseHistory.js.map